import React, { useEffect, useState } from 'react';

import api from 'services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/auth';
import { decryptData } from 'utils/crypto';
import { IDivida } from 'interfaces/Divida';
import { IAcordo, IParcelaAcordo } from 'interfaces/Acordo';
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Typography,
    useMediaQuery
} from '@mui/material';
import { tipoEnvioBoletoEnum } from 'utils/constants';
import DownloadIcon from '@mui/icons-material/Download';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SmsIcon from '@mui/icons-material/Sms';
import { toast } from 'react-toastify';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BackButton from 'components/BackButton';
import EnviarEmailForm from 'forms/EnviarEmailForm';
import EnviarSMSForm from 'forms/EnviarSMSForm';
import { forceFileDownload } from "services/download";

interface IDividaItemProps {
    acordo: IAcordo | null;
    item: IParcelaAcordo | null;
    onClose: any;
    legendaClose: any;
    legendaOpen: any;
}

function PopUpButtons({ item, acordo, onClose, legendaClose, legendaOpen }: IDividaItemProps) {
    const navigate = useNavigate();
    const { user, bank } = useAuth();
    const isMobile = useMediaQuery((th: any) => th?.breakpoints?.down('sm'));
    const [loadingEnviarBoleto, setLoadingEnviarBoleto] = useState(false);
    const [loadingBoleto, setLoadingBoleto] = useState(false);
    const [divida, setDivida] = useState<IDivida>(
        decryptData(localStorage.getItem('@dividaSucesso') || '{}'),
    );
    const [baixarBoleto, setBaixarBoleto] = React.useState(true);
    const [enviarSMS, setEnviarSMS] = React.useState(true);
    const [enviarEmail, setEnviarEmail] = React.useState(true);
    const [baixarAgenda, setBaixarAgenda] = React.useState(true);
    const [tipoEnvio, setTipoEnvio] = React.useState('');
    const [boletoGerado, setBoletoGerado] = React.useState(false);
    const [boletoBaixado, setBoletoBaixado] = React.useState(false);
    const [agendaBaixada, setAgendaBaixada] = React.useState(false);
    const [erroEnvio, setErroEnvio] = React.useState(false);
    const [parcelamento, setParcelamento] = React.useState(false);

    const selectBaixarBoleto = () => {
        setEnviarSMS(false);
        setEnviarEmail(false);
        setBaixarAgenda(false);
        legendaClose();
    };
    const selectEnviarSMS = () => {
        setBaixarBoleto(false);
        setEnviarEmail(false);
        setBaixarAgenda(false);
        legendaClose();
    };
    const selectEnviarEmail = () => {
        setBaixarBoleto(false);
        setEnviarSMS(false);
        setBaixarAgenda(false);
        legendaClose();
    };
    const selectBaixarAgenda = () => {
        setBaixarBoleto(false);
        setEnviarEmail(false);
        setEnviarSMS(false);
        legendaClose();
    };

    const voltarTela = () => {
        setBaixarBoleto(true);
        setEnviarSMS(true);
        setEnviarEmail(true);
        setBaixarAgenda(true);
        setBoletoGerado(false);
        setBoletoBaixado(false);
        setAgendaBaixada(false);
        setErroEnvio(false);
        legendaOpen();
    }

    useEffect(() => {
        if (item?.quantidadeParcelas !== undefined)
            (item?.quantidadeParcelas > 1) ? setParcelamento(true) : setParcelamento(false);
    }, []);

    useEffect(() => {
        function getDividaSucesso() {
            const localDivida = localStorage.getItem('@dividaSucesso');
            const localBoleto = localStorage.getItem('@retornoEmitirBoleto');
            if (!localDivida || !localBoleto) {
                navigate('/dashboard');
                return;
            }
            setDivida(decryptData(localDivida));
        }

        getDividaSucesso();
    }, [navigate]);

    useEffect(() => {
        const mensagemBoletoGerado = () => {
            if (boletoGerado) {
                setEnviarSMS(false);
                setEnviarEmail(false);
                setLoadingBoleto(false);
            }
        }

        mensagemBoletoGerado();
    }, [boletoGerado]);

    useEffect(() => {
        const mensagemBoletoBaixado = () => {
            if (boletoBaixado) {
                setBaixarBoleto(false);
                setLoadingBoleto(false);
            }
        }

        mensagemBoletoBaixado();
    }, [boletoBaixado]);

    useEffect(() => {
        const mensagemErroEnvio = () => {
            if (erroEnvio) {
                setLoadingBoleto(false);
                setBaixarBoleto(false);
                setEnviarSMS(false);
                setEnviarEmail(false);
            }
        }

        mensagemErroEnvio();
    }, [erroEnvio]);

    async function emitirBoletoSegundaVia() {
        const { data: boleto } = await api.post('emitir-boleto-segunda-via', {
            Crm: acordo?.crm,
            Contrato: !!acordo?.contrato && acordo?.contrato[0]?.numero,
            CNPJ_CPF: user?.cnpJ_CPF,
            DataVencimento: item?.dataVencimento,
            CodigoCarteira: acordo?.codCarteira,
            Id: acordo?.idAcordo || item?.id,
            NossoNumero: item?.nossoNumero,
            QuantidadeParcela: acordo?.quantidadeParcelas,
            ValorBoleto: item?.valor,
            TipoBoleto: item?.tipoBoleto,
        });

        return boleto;
    }

    async function enviarBoletos(tipo: number, value: string) {
        setLoadingBoleto(true);
        setTipoEnvio(tipo === tipoEnvioBoletoEnum.EMAIL ? "E-mail"
            : tipo === tipoEnvioBoletoEnum.SMS ? "Sms" : "Whatsapp");

        const boleto = await emitirBoletoSegundaVia();
        const { data } = await api.post('enviar-boleto', {
            GuidBoleto: boleto.guidBoleto,
            cnpJ_CPF: user?.cnpJ_CPF,
            dataVencimento: item?.dataVencimento,
            contrato: !!acordo?.contrato && acordo?.contrato[0]?.numero,
            crm: divida?.crm,
            carteira: divida?.carteiraId,
            tipo: tipo,
            email: tipo === tipoEnvioBoletoEnum.EMAIL ? value : null,
            telefone: tipo === tipoEnvioBoletoEnum.SMS ? value : null,
            CrmIdParaBpMessage: 0,
        });

        if (!data.sucesso) {
            toast.error(boleto.msgRetorno || data.msgRetorno || 'Erro no envio do boleto!');
            setLoadingEnviarBoleto(false);
            setErroEnvio(true);
            return;
        }

        toast.success(data.msgRetorno || 'Boleto enviado com sucesso!');
        setLoadingBoleto(false);
        setBoletoGerado(true);
    }

    const onSubmitForm = async (value: { email: string, telefone: string }) => {
        if (!!value.email) {
            await enviarBoletos(tipoEnvioBoletoEnum.EMAIL, value.email);
        }
        if (!!value.telefone) {
            await enviarBoletos(tipoEnvioBoletoEnum.SMS, value.telefone);
        }
    }

    const onClickBaixarBoleto = async () => {
        try {
            setLoadingBoleto(true);
            selectBaixarBoleto();
            const boleto = await emitirBoletoSegundaVia();

            if (!boleto.sucesso)
                throw new Error(boleto.msgRetorno || 'Tivemos um erro ao recuperar seu boleto, tente novamente em instantes!');

            const response = await api.post(
                'download-boleto',
                {
                    GuidBoleto: boleto.guidBoleto,
                    UrlBoleto: boleto.urlBoleto,
                    cnpj_cpf: user?.cnpJ_CPF,
                    contrato: !!acordo?.contrato && acordo?.contrato[0]?.numero,
                    crm: acordo?.crm,
                    fileName: `Boleto_${!!acordo?.contrato &&
                        acordo?.contrato[0]?.numero
                        }_${new Date().getTime()}.pdf`,
                    carteiraId: acordo?.codCarteira
                },
                {
                    responseType: 'blob',
                },
            );
            const blob = new Blob([response.data], {
                type: 'application/pdf',
            });
            const url = window.URL.createObjectURL(blob);
            window.open(url);

            setLoadingBoleto(false);
            setBoletoBaixado(true);
        } catch (e: any) {
            toast.error(e.message);
            setLoadingBoleto(false);
            setErroEnvio(true);
            return;
        }
    };

    const downloadAgenda = async () => {
        try {
            setLoadingBoleto(true);
            selectBaixarAgenda();
            const response = await api.post(
                'download-agenda',
                {
                    CnpjCpf: user?.cnpJ_CPF,
                },
            );

            forceFileDownload(response, `${user?.nome}_${new Date().getTime()}.ics`);

            if (response?.status == 200) {
                toast.success('Agenda baixada com sucesso!');
            }

        } finally {
            setAgendaBaixada(true);
            setLoadingBoleto(false);
        }
    };

    return (
        <div>

            {baixarBoleto && !loadingBoleto && (//baixar boleto
                <Grid item xs={12} sm={12} style={{ padding: '0.5em 0 0.25em 0' }}>
                    <Box display="flex" justifyContent="center">
                        <Button
                            style={{ textTransform: 'none', height: 44, maxWidth: '30em', minWidth: '200px' }}
                            variant="contained"
                            color="primary"
                            disabled={loadingBoleto}
                            onClick={onClickBaixarBoleto}
                            size={isMobile ? 'small' : 'medium'}
                        >
                            <DownloadIcon />Baixar aqui
                        </Button>
                    </Box>
                </Grid>
            )}

            {enviarEmail && !loadingBoleto && (//enviar EMAIL
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ padding: '0.25em 0' }}
                >
                    <Grid item xs={12} sm={12}>
                        <Box display="flex" justifyContent="center">
                            {enviarEmail && enviarSMS && !loadingBoleto && (
                                <Button
                                    style={{ textTransform: 'none', height: 44, minWidth: '200px' }}
                                    variant="contained"
                                    color="primary"
                                    onClick={() => selectEnviarEmail()}
                                    disabled={loadingEnviarBoleto}
                                >
                                    <MailOutlineIcon />Enviar por e-mail
                                </Button>
                            )}
                            {enviarEmail && !enviarSMS && !baixarBoleto && (
                                <Typography variant="body1" sx={{ color: "#6C757D" }}>
                                    <strong>Enviar por e-mail</strong>
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                    {enviarEmail && !enviarSMS && !baixarBoleto && (
                        <Grid
                            container
                            direction="row"
                            justifyContent="center"
                            alignItems="center"
                            spacing={2}
                        >
                            <Grid item xs={12} sm={12}>
                                <Box display="flex" justifyContent="center">
                                    <Typography variant="body2" sx={{ color: "#6C757D" }}>
                                        <br /><strong>Digite o endereço de e-mail</strong>
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <Box display="flex" justifyContent="center" >
                                    <EnviarEmailForm onSubmit={onSubmitForm} />
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            )}

            {enviarSMS && !loadingBoleto && (//enviar SMS
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ padding: '0.25em 0' }}
                >
                    <Grid item xs={12} sm={12}>
                        <Box display="flex" justifyContent="center">
                            {enviarEmail && enviarSMS && !loadingBoleto && (
                                <Button
                                    style={{ textTransform: 'none', height: 44, minWidth: '200px' }}
                                    variant="contained"
                                    color="primary"
                                    onClick={() => selectEnviarSMS()}
                                    disabled={loadingEnviarBoleto}
                                >
                                    <SmsIcon />Enviar por SMS
                                </Button>
                            )}
                            {!enviarEmail && enviarSMS && !baixarBoleto && (
                                <Box>
                                    <Typography variant="body1" sx={{ color: "#6C757D" }}>
                                        <strong>Enviar por SMS</strong>
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                    {enviarSMS && !enviarEmail && !baixarBoleto && (
                        <Grid
                            container
                            direction="row"
                            justifyContent="center"
                            alignItems="center"
                            spacing={2}
                        >
                            <Grid item xs={12} sm={12}>
                                <Box display="flex" justifyContent="center">
                                    <Typography variant="body2" sx={{ color: "#6C757D" }}>
                                        <br /><strong>Digite o telefone celular</strong>
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="center" alignItems="center">
                                    <EnviarSMSForm onSubmit={onSubmitForm} />
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            )}

            {baixarAgenda && !loadingBoleto && parcelamento && (//baixar agenda
                <Grid item xs={12} sm={12} style={{ padding: '0.25em 0' }}>
                    <Box display="flex" justifyContent="center">
                        {enviarEmail && enviarSMS && !loadingBoleto && (
                            <Button
                                style={{ textTransform: 'none', height: 44, maxWidth: '30em', minWidth: '200px' }}
                                variant="contained"
                                color="primary"
                                disabled={loadingBoleto}
                                onClick={downloadAgenda}
                                size={isMobile ? 'small' : 'medium'}
                            >
                                <DownloadIcon />Baixar AGENDA
                            </Button>
                        )}
                    </Box>
                </Grid>
            )}

            {loadingBoleto && (//mensagem baixando/enviando
                <Grid item xs={12} sm={12}>
                    <Box display="flex" justifyContent="center" style={{ padding: '1.5em 0 0 0' }}>
                        <CircularProgress size={70} />
                    </Box>
                    <Box
                        display="flex"
                        justifyContent="center"
                        marginTop="10%"
                        marginBottom="10%"
                    >
                        {baixarAgenda ? "Baixando Agenda" : (baixarBoleto ? "Baixando a 2ª Via" : (`Enviando a 2ª via por ${tipoEnvio}`))}
                    </Box>
                </Grid>
            )}

            {(boletoGerado || boletoBaixado) && (//mensagem sucesso boleto
                <Grid item xs={12} sm={12}>
                    <Box
                        style={{ color: "#4DCC70" }}
                        display="flex"
                        justifyContent="center"
                        marginTop="10%"
                        marginBottom="10%"
                    >
                        <CheckCircleOutlineIcon style={{ marginRight: "3%" }} />
                        <Typography>
                            Boleto {boletoBaixado ? "baixado" : "enviado"} com sucesso!
                        </Typography>
                    </Box>
                </Grid>
            )}

            {agendaBaixada && (//mensagem sucesso agenda
                <Grid item xs={12} sm={12}>
                    <Box
                        style={{ color: "#4DCC70" }}
                        display="flex"
                        justifyContent="center"
                        marginTop="10%"
                        marginBottom="10%"
                    >
                        <CheckCircleOutlineIcon style={{ marginRight: "3%" }} />
                        <Typography>
                            Agenda baixada com sucesso!
                        </Typography>
                    </Box>
                </Grid>
            )}

            {erroEnvio && (//mensagem erro
                <Grid
                    container
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                >
                    <Grid item xs={12} sm={12}>
                        <Box display="flex" justifyContent="center" marginBottom="5%">
                            Não conseguimos pegar seu boleto por meio dessa opção.
                        </Box>
                        <Box display="flex" justifyContent="center" marginBottom="5%">
                            <strong>Você gostaria de gerar de outra forma?</strong>
                        </Box>
                        <Box display="flex" justifyContent="center">
                            <Box marginRight="5%">
                                <Button variant="outlined" color="primary" onClick={onClose}>Não</Button>
                            </Box>
                            <Button variant="contained" color="secondary" onClick={voltarTela}>Sim</Button>
                        </Box>
                    </Grid>
                </Grid>
            )}

            {(!enviarEmail || !enviarSMS || !baixarBoleto) && (//botão Voltar
                <BackButton
                    disabled={loadingBoleto}
                    onClick={() => voltarTela()}
                >
                    Voltar
                </BackButton>
            )}
        </div>
    )
}

export default PopUpButtons;
